import { normalizeComments } from "./comments.js";

export const FORMAT_VERSION = 4;
export class UnsupportedFormatError extends Error {
  constructor() {
    super("This canvas uses a newer format. Reload the latest app to open it; its saved data has not changed.");
    this.name = 'UnsupportedFormatError';
  }
}
export const SIDES = ["left", "right", "top", "bottom"];
export const COLOURS = {
  1: "#e05252",
  2: "#e59b48",
  3: "#d3d467",
  4: "#52c878",
  5: "#4bc3c7",
  6: "#ab87e3",
};
export const TYPES = {
  agent: "Agent",
  prompt: "Prompt",
  source: "Source",
  artifact: "Artifact",
  human: "Human decision",
  memory: "Memory",
  tool: "Tool",
  handoff: "Handoff",
  note: "Note",
  custom: "Custom",
};
export const RELATIONS = {
  data: "Data flow",
  context: "Context",
  control: "Control",
  review: "Review",
  memory: "Memory",
  rejection: "Rejection",
  handoff: "Handoff",
};
const KIND_COLOURS = {
  data: "4",
  context: "5",
  control: "6",
  review: "3",
  memory: "2",
  rejection: "1",
  handoff: "6",
};

export function parseNote(raw = "") {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/);
  const properties = match
    ? match[1].split("\n").flatMap((line) => {
        const colon = line.indexOf(":");
        return colon > 0
          ? [
              [
                line.slice(0, colon).trim(),
                propertyValue(line.slice(colon + 1).trim()),
              ],
            ]
          : [];
      })
    : [];
  return {
    properties,
    body: match ? raw.slice(match[0].length) : raw,
  };
}

function propertyValue(value) {
  if (value.startsWith('"')) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "string") return parsed;
    } catch {}
  }
  return value.replace(/^'(.*)'$/, "$1");
}

export function titleFromText(text, fallback = "Untitled") {
  return (
    text
      .split("\n")
      .find((line) => line.trim())
      ?.replace(/^#+\s*/, "")
      .replace(/\*\*/g, "")
      .slice(0, 120) || fallback
  );
}

export function colourValue(value) {
  return (
    COLOURS[value] || (/^#[\da-f]{6}$/i.test(value || "") ? value : "#898989")
  );
}

function string(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}
function number(value, fallback, limit = 1000000) {
  if (value === undefined) return fallback;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    Math.abs(value) > limit
  )
    throw new Error("Card positions and sizes must be finite numbers.");
  return value;
}

// Translate either Obsidian JSON Canvas or our earlier prototype before changing the active document.
export function normalizeDocument(input, bundledFiles = {}) {
  if (!input || typeof input !== "object" || Array.isArray(input))
    throw new Error("This file does not contain a canvas.");
  if (input.kind && input.kind !== "agent-architecture-canvas")
    throw new Error("This is a different document format.");
  if (Number.isInteger(input.schemaVersion) && input.schemaVersion > FORMAT_VERSION) throw new UnsupportedFormatError();
  if (
    input.schemaVersion !== undefined &&
    (!Number.isInteger(input.schemaVersion) ||
      input.schemaVersion < 1 ||
      input.schemaVersion > FORMAT_VERSION)
  )
    throw new Error(
      "This canvas uses an unsupported format version. Your existing canvases have not changed.",
    );
  const original = input.canvas || input;
  const raw = {
    ...original,
    nodes: original.nodes ?? [],
    edges: original.edges ?? original.connections ?? [],
  };
  if (
    !raw ||
    !Array.isArray(raw.nodes) ||
    !Array.isArray(raw.edges || raw.connections)
  )
    throw new Error(
      "Choose a .canvas file or an exported architecture JSON file.",
    );
  if (raw.nodes.length > 2000 || (raw.edges || raw.connections).length > 5000)
    throw new Error(
      "A canvas can contain up to 2,000 cards and 5,000 connections.",
    );
  const files = input.files || bundledFiles;
  const ids = new Set();
  const nodes = raw.nodes.map((n) => {
    if (!n || typeof n.id !== "string" || !n.id || ids.has(n.id))
      throw new Error("Every card must have a unique ID.");
    ids.add(n.id);
    const kind = ["file", "group", "link"].includes(n.type) ? n.type : "text";
    const legacy = !["text", "file", "group", "link"].includes(n.type);
    const file = string(n.file);
    const rawNote = string(n.rawNote, string(files[file]));
    const noteAvailable =
      typeof n.rawNote === "string" ||
      (Object.hasOwn(files, file) && typeof files[file] === "string");
    const parsed = parseNote(rawNote);
    const missing = kind === "file" && !noteAvailable;
    const text =
      kind === "file"
        ? noteAvailable
          ? parsed.body
          : `# ${file.split("/").pop()?.replace(/\.md$/, "") || "Linked note"}\n\nThis linked note is not included in the canvas file. Import a bundled JSON export to include its contents.`
        : legacy
          ? `## ${string(n.title, "Untitled")}\n\n${string(n.summary)}${n.contract ? "\n\n`" + string(n.contract) + "`" : ""}${n.prompt ? "\n\n## Prompt\n\n" + string(n.prompt) : ""}`
          : string(n.text, string(n.label, string(n.url)));
    const width = number(n.width, kind === "file" ? 440 : 360, 20000);
    const height = number(n.height, kind === "file" ? 560 : 300, 20000);
    if (width < 80 || height < 40)
      throw new Error("Cards must be at least 80 × 40.");
    const role = string(
      n.role,
      legacy ? n.type : kind === "file" ? "agent" : "note",
    );
    return {
      id: n.id,
      type: kind,
      role: TYPES[role] ? role : "custom",
      title: string(
        n.title,
        kind === "file"
          ? file.split("/").pop().replace(/\.md$/, "")
          : titleFromText(text),
      ),
      text,
      file,
      url: string(n.url),
      subpath: string(n.subpath),
      background: string(n.background),
      backgroundStyle: string(n.backgroundStyle),
      contract: {
        inputs: string(n.contract?.inputs),
        outputs: string(n.contract?.outputs),
        variables: string(n.contract?.variables),
        notes: string(n.contract?.notes),
      },
      extensions:
        n.extensions &&
        typeof n.extensions === "object" &&
        !Array.isArray(n.extensions)
          ? structuredClone(n.extensions)
          : {},
      properties: parsed.properties.length
        ? parsed.properties
        : Array.isArray(n.properties)
          ? n.properties
              .filter((p) => Array.isArray(p) && p.length === 2)
              .map((p) => p.map((v) => string(v)))
          : [],
      x: number(n.x, 0),
      y: number(n.y, 0),
      width,
      height,
      color: string(n.color),
      missing,
    };
  });
  const edgeIds = new Set();
  const edges = (raw.edges || raw.connections).map((e) => {
    if (!e || typeof e.id !== "string" || !e.id || edgeIds.has(e.id))
      throw new Error("Every connection must have a unique ID.");
    edgeIds.add(e.id);
    const source = e.fromNode || e.source,
      target = e.toNode || e.target;
    if (!ids.has(source) || !ids.has(target))
      throw new Error("A connection points to a missing card.");
    const sourceSide = e.fromSide || e.sourceSide || "right",
      targetSide = e.toSide || e.targetSide || "left";
    if (!SIDES.includes(sourceSide) || !SIDES.includes(targetSide))
      throw new Error(
        "Connections must attach to the left, right, top, or bottom of a card.",
      );
    return {
      id: e.id,
      source,
      target,
      sourceSide,
      targetSide,
      label: string(e.label),
      kind: RELATIONS[e.kind]
        ? e.kind
        : {
            1: "rejection",
            2: "memory",
            3: "review",
            4: "data",
            5: "context",
            6: "control",
          }[e.color] || "data",
      color: string(e.color, KIND_COLOURS[e.kind]),
      end: e.toEnd === "none" ? "none" : "arrow",
      start: e.fromEnd === "arrow" ? "arrow" : "none",
    };
  });
  return {
    id: string(input.id) || crypto.randomUUID(),
    title: string(input.title, "Untitled canvas").trim() || "Untitled canvas",
    project: string(input.project, "My canvases"),
    comments: normalizeComments(input.comments),
    nodes,
    edges,
  };
}

export function anchor(node, side) {
  return {
    x:
      node.x +
      (side === "left" ? 0 : side === "right" ? node.width : node.width / 2),
    y:
      node.y +
      (side === "top" ? 0 : side === "bottom" ? node.height : node.height / 2),
  };
}
const VECTOR = { left: [-1, 0], right: [1, 0], top: [0, -1], bottom: [0, 1] };
export function curve(
  source,
  target,
  sourceSide = "right",
  targetSide = "left",
) {
  const distance = Math.max(
    48,
    Math.hypot(target.x - source.x, target.y - source.y) * 0.45,
  );
  const a = VECTOR[sourceSide],
    b = VECTOR[targetSide];
  const p1 = { x: source.x + a[0] * distance, y: source.y + a[1] * distance };
  const p2 = { x: target.x + b[0] * distance, y: target.y + b[1] * distance };
  return {
    path: `M ${source.x} ${source.y} C ${p1.x} ${p1.y}, ${p2.x} ${p2.y}, ${target.x} ${target.y}`,
    middle: {
      x: (source.x + 3 * p1.x + 3 * p2.x + target.x) / 8,
      y: (source.y + 3 * p1.y + 3 * p2.y + target.y) / 8,
    },
  };
}

export function exportDocument(doc) {
  const files = {};
  const nodes = doc.nodes.map((n) => {
    if (n.type === "file" && !n.missing)
      files[n.file] =
        (n.properties.length
          ? "---\n" +
            n.properties
              .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
              .join("\n") +
            "\n---\n"
          : "") + n.text;
    return {
      id: n.id,
      type: n.type,
      x: n.x,
      y: n.y,
      width: n.width,
      height: n.height,
      role: n.role,
      title: n.title,
      contract: n.contract || {
        inputs: "",
        outputs: "",
        variables: "",
        notes: "",
      },
      extensions: n.extensions || {},
      ...(n.url ? { url: n.url } : {}),
      ...(n.subpath ? { subpath: n.subpath } : {}),
      ...(n.background ? { background: n.background } : {}),
      ...(n.backgroundStyle ? { backgroundStyle: n.backgroundStyle } : {}),
      ...(n.type === "file"
        ? { file: n.file }
        : n.type === "group"
          ? { label: n.title, text: n.text, properties: n.properties }
          : { text: n.text, properties: n.properties }),
      ...(n.color ? { color: n.color } : {}),
    };
  });
  const edges = doc.edges.map((e) => ({
    id: e.id,
    fromNode: e.source,
    fromSide: e.sourceSide,
    toNode: e.target,
    toSide: e.targetSide,
    label: e.label,
    color: e.color,
    toEnd: e.end,
    fromEnd: e.start || "none",
    kind: e.kind,
  }));
  return {
    kind: "agent-architecture-canvas",
    schemaVersion: FORMAT_VERSION,
    id: doc.id,
    title: doc.title,
    project: doc.project,
    comments: normalizeComments(doc.comments),
    canvas: { nodes, edges },
    files,
  };
}

export function exportNativeCanvas(doc) {
  const canvas = exportDocument(doc).canvas;
  for (const node of canvas.nodes)
    for (const key of ["x", "y", "width", "height"])
      node[key] = Math.round(node[key]);
  return canvas;
}

export function escapeHtml(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function inline(text) {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(
      /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g,
      (_, path, label) =>
        `<button class="wiki-link" data-note-link="${path}">${label || path.split("/").pop().replace(/\.md$/, "")}</button>`,
    );
}

// Small, escaped Markdown renderer for canvas notes; document text is never interpreted as HTML or code.
export function markdown(text = "") {
  const lines = text.split("\n");
  let html = "",
    list = "",
    table = false,
    code = false;
  const closeList = () => {
    if (list) {
      html += `</${list}>`;
      list = "";
    }
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("```")) {
      closeList();
      html += code ? "</code></pre>" : "<pre><code>";
      code = !code;
      continue;
    }
    if (code) {
      html += escapeHtml(line) + "\n";
      continue;
    }
    if (line.startsWith("|")) {
      closeList();
      if (!table) {
        html += "<table><tbody>";
        table = true;
      }
      if (/^\|[\s:|\-]+\|$/.test(line)) continue;
      html +=
        "<tr>" +
        line
          .replace(/^\||\|$/g, "")
          .split(/(?<!\\)\|/)
          .map((c) => `<td>${inline(c.trim())}</td>`)
          .join("") +
        "</tr>";
      continue;
    }
    if (table) {
      html += "</tbody></table>";
      table = false;
    }
    const item = line.match(/^\s*(?:([-*]) |(\d+)\. )(.*)$/);
    if (item) {
      const tag = item[2] ? "ol" : "ul";
      if (list !== tag) {
        closeList();
        html += `<${tag}>`;
        list = tag;
      }
      html += `<li>${inline(item[3])}</li>`;
      continue;
    }
    closeList();
    const heading = line.match(/^(#{1,4}) (.*)$/);
    if (heading) {
      html += `<h${heading[1].length}>${inline(heading[2])}</h${heading[1].length}>`;
      continue;
    }
    if (line.trim() === "---") {
      html += "<hr>";
      continue;
    }
    if (line.trim()) html += `<p>${inline(line)}</p>`;
  }
  closeList();
  if (table) html += "</tbody></table>";
  if (code) html += "</code></pre>";
  return html;
}
